/**
 * Recipe Matching Logic
 * 
 * Matches pantry items against recipe requirements.
 * Returns recipes sorted by how well they match available ingredients.
 */

import { RECIPES_BY_EFFORT, ALL_RECIPES } from '../data/recipes';
import { getCanonicalIngredient } from '../data/ingredientAliases';

/**
 * Check if pantry contains an ingredient
 * Uses canonical ingredient matching for flexibility
 */
function pantryHasIngredient(pantryItems, recipeIngredient) {
  const canonicalNeeded = getCanonicalIngredient(recipeIngredient);
  
  return pantryItems.some(item => {
    const canonicalHave = getCanonicalIngredient(item.name);
    const category = (item.category || '').toLowerCase();
    
    // Direct canonical match
    if (canonicalHave === canonicalNeeded) {
      return true;
    }
    
    // Partial match (e.g., "rotisserie chicken" contains "chicken")
    // But NOT the reverse - "chicken" shouldn't match "rotisserie chicken" recipes
    if (canonicalHave.includes(canonicalNeeded) && canonicalHave !== canonicalNeeded) {
      // "rotisserie chicken" can satisfy "chicken" recipes
      // Check if it's a more specific version of the needed ingredient
      return true;
    }
    
    // Category match for generic recipe requirements
    if (canonicalNeeded === 'protein' && ['protein', 'meat'].includes(category)) {
      return true;
    }
    if (canonicalNeeded === 'veg' && ['vegetable', 'veg'].includes(category)) {
      return true;
    }
    if (canonicalNeeded === 'meat' && ['protein', 'meat'].includes(category)) {
      return true;
    }
    
    return false;
  });
}

/**
 * Find recipes that match pantry items
 * 
 * @param {Array} pantryItems - Items from useInventory hook
 * @param {string} effortLevel - 'no-cook' | 'one-pan' | 'some-steps' | 'full-cook' | 'all'
 * @param {Object} options - Additional filtering options
 * @returns {Array} Sorted recipes with match info
 */
export function findMatchingRecipes(pantryItems, effortLevel = 'all', options = {}) {
  const { maxMissing = 2, minMatch = 0 } = options;
  
  // Get recipes for effort level
  const recipes = effortLevel === 'all' 
    ? ALL_RECIPES 
    : (RECIPES_BY_EFFORT[effortLevel] || []);
  
  if (!pantryItems || pantryItems.length === 0) {
    // No pantry items - return all recipes with empty match info
    return recipes.map(recipe => ({
      ...recipe,
      canMake: false,
      matchScore: 0,
      hasIngredients: [],
      missingRequired: recipe.needs,
      optionalMatches: []
    }));
  }
  
  // Score each recipe
  const scoredRecipes = recipes.map(recipe => {
    // Check required ingredients
    const hasIngredients = recipe.needs.filter(need => 
      pantryHasIngredient(pantryItems, need)
    );
    const missingRequired = recipe.needs.filter(need => 
      !pantryHasIngredient(pantryItems, need)
    );
    
    // Check optional ingredients
    const optionalMatches = (recipe.optional || []).filter(opt =>
      pantryHasIngredient(pantryItems, opt)
    );
    
    // Can make if all required ingredients present
    const canMake = missingRequired.length === 0;
    const almostCanMake = missingRequired.length <= maxMissing;
    
    // Score: required matches + optional matches (weighted)
    const matchScore = hasIngredients.length * 2 + optionalMatches.length;
    
    return {
      ...recipe,
      canMake,
      almostCanMake,
      matchScore,
      hasIngredients,
      missingRequired,
      optionalMatches
    };
  });
  
  // Filter and sort
  return scoredRecipes
    .filter(r => r.canMake || r.almostCanMake)
    .filter(r => r.matchScore >= minMatch)
    .sort((a, b) => {
      // Prioritize: can make > almost can make
      if (a.canMake && !b.canMake) return -1;
      if (!a.canMake && b.canMake) return 1;
      // Then by match score
      return b.matchScore - a.matchScore;
    });
}

/**
 * Get recipe suggestions formatted for display
 * 
 * @param {Array} pantryItems - Items from useInventory hook
 * @param {string} effortLevel - Effort level filter
 * @returns {Object} { canMake: [], almostCanMake: [] }
 */
export function getRecipeSuggestions(pantryItems, effortLevel = 'all') {
  const matches = findMatchingRecipes(pantryItems, effortLevel);
  
  return {
    canMake: matches.filter(r => r.canMake),
    almostCanMake: matches.filter(r => !r.canMake && r.almostCanMake)
  };
}

/**
 * Format ingredient list for display
 * "You have: X, Y, Z"
 */
export function formatHasIngredients(recipe) {
  if (!recipe.hasIngredients || recipe.hasIngredients.length === 0) {
    return null;
  }
  return `You have: ${recipe.hasIngredients.join(', ')}`;
}

/**
 * Format missing ingredients for display
 * "Missing: X, Y"
 */
export function formatMissingIngredients(recipe) {
  if (!recipe.missingRequired || recipe.missingRequired.length === 0) {
    return null;
  }
  return `Missing: ${recipe.missingRequired.join(', ')}`;
}

export default findMatchingRecipes;
